import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { HandoffForm } from '@/Domain/components/Action/HandoffAction/HandoffAction.sheet';
import { AutoSizeInput } from '@/Domain/components/Shared/AutosizeInput/AutosizeInput';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { FormDescription, FormField, FormLabel } from '@/Domain/components/Shared/Form/Form';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import AddIcon from '@/Domain/components/icons/AddIcon';

type Props = {
  form: UseFormReturn<HandoffForm>;
};

const MAX_KEYWORDS = 100;
export const KeywordsField = ({ form }: Props) => {
  const { t } = useTranslation('handoff_action');

  const { fields, append, remove } = useFieldArray({
    name: 'settings.keywords',
    control: form.control,
  });

  const handleAddField = () => append({ value: '' }, { focusIndex: fields.length });
  const handelRemoveField = (index: number) => remove(index);

  return (
    <div id="keywords" className="hidden">
      <FormLabel>{t('form.keywords_label')}</FormLabel>
      <FormDescription>{t('form.keywords_description')}</FormDescription>

      <div className="flex items-center flex-wrap gap-2 mt-2.5">
        {fields.map((arrayField, index) => (
          <FormField
            key={arrayField.id}
            name={`settings.keywords.${index}.value`}
            render={({ field }) => (
              <Badge onClose={() => handelRemoveField(index)} size="lg" variant="secondary">
                <AutoSizeInput
                  {...field}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      return handleAddField();
                    }
                  }}
                />
              </Badge>
            )}
          />
        ))}

        <Tooltip open={fields.length >= MAX_KEYWORDS ? undefined : false}>
          <TooltipTrigger asChild>
            <div>
              <Button
                type="button"
                variant="link"
                onClick={handleAddField}
                className="!pl-0"
                disabled={fields.length >= MAX_KEYWORDS}
              >
                <AddIcon />
                {t('button.add_keyword')}
              </Button>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {t('tooltip.max_keywords_reached', { max: MAX_KEYWORDS })}
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};
